import { Injectable } from '@angular/core';
import { AngularFireDatabase, AngularFireList, AngularFireObject, PathReference, QueryFn } from '@angular/fire/compat/database';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { deepCamelCase } from './camel-case.util';

/**
 * Single RTDB entry point for public and cem. Path strings and AngularFireDatabase
 * stay in this class; callers use named methods (same idea as BackendApiService).
 */
@Injectable({ providedIn: 'root' })
export class SpaqFireDb {
	readonly camelCaseExceptions: string[] = [
		'language/fr',
		'language/en',
		'/vehicles/year',
		'/faq',
		'/config/maintenanceScopes',
		'/config/killSwitchScope'
	];

	constructor(private db: AngularFireDatabase) {}

	private shouldCamelCase(pathOrRef: PathReference): boolean {
		const path = typeof pathOrRef === 'string' ? pathOrRef : pathOrRef.toString();
		return !this.camelCaseExceptions.some(exc => path.includes(exc));
	}

	private list<T = any>(pathOrRef: PathReference, queryFn?: QueryFn): AngularFireList<T> {
		const ref = this.db.list<T>(pathOrRef, queryFn);
		return {
			query: ref.query,
			valueChanges: ((...args: any[]) =>
				ref.valueChanges(...args).pipe(
					map(data => (this.shouldCamelCase(pathOrRef) ? deepCamelCase(data) : data)),
					tap(data => this.onMessage('list:valueChanges', pathOrRef, data))
				)) as AngularFireList<T>['valueChanges'],
			snapshotChanges: (events?: any) =>
				ref.snapshotChanges(events).pipe(tap(data => this.onMessage('list:snapshotChanges', pathOrRef, data))),
			stateChanges: (events?: any) =>
				ref.stateChanges(events).pipe(tap(data => this.onMessage('list:stateChanges', pathOrRef, data))),
			auditTrail: (events?: any) => ref.auditTrail(events).pipe(tap(data => this.onMessage('list:auditTrail', pathOrRef, data))),
			update: (item, data) => ref.update(item, data),
			set: (item, data) => ref.set(item, data),
			push: data => ref.push(data),
			remove: item => ref.remove(item)
		};
	}

	private object<T = any>(pathOrRef: PathReference): AngularFireObject<T> {
		const ref = this.db.object<T>(pathOrRef);
		return {
			query: ref.query,
			valueChanges: () =>
				ref.valueChanges().pipe(
					map(data => (this.shouldCamelCase(pathOrRef) ? deepCamelCase(data) : data)),
					tap(data => this.onMessage('object:valueChanges', pathOrRef, data))
				),
			snapshotChanges: () => ref.snapshotChanges().pipe(tap(data => this.onMessage('object:snapshotChanges', pathOrRef, data))),
			update: data => ref.update(data),
			set: data => ref.set(data),
			remove: () => ref.remove()
		};
	}

	private onMessage(operation: string, path: PathReference, data: unknown): void {
		const w = window as any;
		if (w.jsonLens) {
			w.jsonLens.write(operation, data, { path: path });
		}
	}

	goOffline(): void {
		this.db.database.goOffline();
	}

	goOnline(): void {
		this.db.database.goOnline();
	}

	killSwitch$(path: string): Observable<any> {
		return this.object(path).valueChanges();
	}

	user$<T>(uid: string): Observable<T> {
		return this.object<T>(`/user/${uid}`).valueChanges();
	}

	updateUser(uid: string, patch: any): Promise<void> {
		return this.object(`/user/${uid}`).update(patch);
	}

	userVehicles$<T>(uid: string): Observable<T> {
		return this.object<T>(`/user/${uid}/vehicles`).valueChanges();
	}

	userVehiclesList$<T>(uid: string): Observable<T[]> {
		return this.list<T>(`/user/${uid}/vehicles`).valueChanges();
	}

	removeUserVehicles(uid: string): Promise<void> {
		return this.object(`/user/${uid}/vehicles`).remove();
	}

	userContract$<T>(uid: string): Observable<T> {
		return this.object<T>(`/user/${uid}/contract`).valueChanges();
	}

	setUserContract(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/contract`).set(value);
	}

	updateUserContract(uid: string, patch: any): Promise<void> {
		return this.object(`/user/${uid}/contract`).update(patch);
	}

	userContractServices$(uid: string): Observable<any> {
		return this.object<any>(`/user/${uid}/contract/services`).valueChanges();
	}

	userContractAvailableServiceId$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/contract/service/availableServiceId`).valueChanges();
	}

	userContractParkIdentificationNo$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/contract/service/parkIdentificationNo`).valueChanges();
	}

	userContractSubscriptionService$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/contract/subscriptionService`).valueChanges();
	}

	setUserContractMode(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/contract/mode`).set(value);
	}

	setUserContractHaveAccessCard(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/contract/haveAccessCard`).set(value);
	}

	setUserContractAccessCardNumber(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/contract/subscription/custom/numero_acces`).set(value);
	}

	setUserContractWaitingList(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/contract/subscription/custom/liste_attente`).set(value);
	}

	setUserContractPickupLocation(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/contract/subscription/custom/lieu_de_recuperation`).set(value);
	}

	userAccessCode$<T>(uid: string): Observable<T> {
		return this.object<T>(`/user/${uid}/access-code`).valueChanges();
	}

	setUserAccessCode(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/access-code`).set(value);
	}

	updateUserAccessCode(uid: string, patch: any): Promise<void> {
		return this.object(`/user/${uid}/access-code`).update(patch);
	}

	removeUserAccessCode(uid: string): Promise<void> {
		return this.object(`/user/${uid}/access-code`).remove();
	}

	userSubscription$<T>(uid: string): Observable<T> {
		return this.object<T>(`/user/${uid}/subscription`).valueChanges();
	}

	setUserSubscriptionCustom(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/subscription/custom`).set(value);
	}

	updateUserSubscriptionCustom(uid: string, patch: any): Promise<void> {
		return this.object(`/user/${uid}/subscription/custom`).update(patch);
	}

	setUserSubscriptionAccessCardNumber(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/subscription/custom/numero_acces`).set(value);
	}

	setUserSubscriptionWaitingList(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/subscription/custom/liste_attente`).set(value);
	}

	setUserSubscriptionPickupLocation(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/subscription/custom/lieu_de_recuperation`).set(value);
	}

	userSpaqContractNo$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/subscription/spaq/contractNo`).valueChanges();
	}

	userAccountStatus$<T>(uid: string): Observable<T> {
		return this.object<T>(`/user/${uid}/accountStatus`).valueChanges();
	}

	updateUserAccountStatus(uid: string, status: any): Promise<void> {
		return this.object(`/user/${uid}/accountStatus`).update(status);
	}

	setSubscriptionRequired(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/accountStatus/isSubscriptionRequired`).set(value);
	}

	userMetadataSnapshot$(uid: string) {
		return this.object(`/user/${uid}/metadata`).snapshotChanges();
	}

	userMonerisVault$<T>(uid: string): Observable<T> {
		return this.object<T>(`/user/${uid}/moneris/vault`).valueChanges();
	}

	userPayment$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/payment`).valueChanges();
	}

	userPaymentPpa$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/payment/ppa`).valueChanges();
	}

	removeUserPaymentCards(uid: string): Promise<void> {
		return this.object(`/user/${uid}/payment/cards`).remove();
	}

	userBooking$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/booking`).valueChanges();
	}

	setUserBooking(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/booking`).set(value);
	}

	userCalendarBlockedUntil$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/calendarIsBlockedUntil`).valueChanges();
	}

	setUserCalendarBlockedUntil(uid: string, value: any): Promise<void> {
		return this.object(`/user/${uid}/calendarIsBlockedUntil`).set(value);
	}

	userHost$(uid: string): Observable<any> {
		return this.object(`/user/${uid}/host`).valueChanges();
	}

	completedContracts$(): Observable<any[]> {
		return this.list(`/user`, ref => ref.orderByChild('contract/isCreationCompleted').equalTo(true)).valueChanges();
	}

	accessCodeEntry$(companyCode: string, userCode: string): Observable<any> {
		return this.object(`/access-code/${companyCode}/${userCode}`).valueChanges();
	}

	accessCodeCatalogSnapshot$(companyCode: string, userCode: string) {
		return this.object(`/access-code/${companyCode}/codes/${userCode}`).snapshotChanges();
	}

	accessCodeCompanyName$(companyCode: string): Observable<any> {
		return this.object(`/access-code/${companyCode}/name`).valueChanges();
	}

	accessCodes$<T>(): Observable<T[]> {
		return this.list<T>('/access-code').valueChanges();
	}

	orgGroupAccessCodes$<T>(organizationCode: string, groupCode: string): Observable<T> {
		return this.object<T>(`/access-code/${organizationCode}-${groupCode}`).valueChanges();
	}

	setOrgGroupAccessCodes(organizationCode: string, groupCode: string, value: any): Promise<void> {
		return this.object(`/access-code/${organizationCode}-${groupCode}`).set(value);
	}

	orgGroupUserAccessCode$<T>(organizationCode: string, groupCode: string, userCode: string): Observable<T> {
		return this.object<T>(`/access-code/${organizationCode}-${groupCode}/${userCode}`).valueChanges();
	}

	setOrgGroupUserAccessCode(organizationCode: string, groupCode: string, userCode: string, value: any): Promise<void> {
		return this.object(`/access-code/${organizationCode}-${groupCode}/${userCode}`).set(value);
	}

	updateOrgGroupUserAccessCode(organizationCode: string, groupCode: string, userCode: string, patch: any): Promise<void> {
		return this.object(`/access-code/${organizationCode}-${groupCode}/${userCode}`).update(patch);
	}

	removeOrgGroupUserAccessCode(organizationCode: string, groupCode: string, userCode: string): Promise<void> {
		return this.object(`/access-code/${organizationCode}-${groupCode}/${userCode}`).remove();
	}

	organizations$<T>(): Observable<T[]> {
		return this.list<T>('/organizations').valueChanges();
	}

	organizationsSnapshot$() {
		return this.list(`/organizations`).snapshotChanges();
	}

	organization$<T>(id: string): Observable<T> {
		return this.object<T>(`/organizations/${id}`).valueChanges();
	}

	organizationSnapshot$(id: string) {
		return this.object(`/organizations/${id}`).snapshotChanges();
	}

	setOrganization(id: string, value: any): Promise<void> {
		return this.object(`/organizations/${id}`).set(value);
	}

	updateOrganization(id: string, patch: any): Promise<void> {
		return this.object(`/organizations/${id}`).update(patch);
	}

	removeOrganization(id: string): Promise<void> {
		return this.object(`/organizations/${id}`).remove();
	}

	organizationConfig$<T>(id: string): Observable<T> {
		return this.object<T>(`/organizations/${id}/config`).valueChanges();
	}

	organizationGroups$<T>(organizationCode: string): Observable<T[]> {
		return this.list<T>(`/organizations/${organizationCode}/groups`).valueChanges();
	}

	organizationGroupsSnapshot$(organizationCode: string) {
		return this.list(`/organizations/${organizationCode}/groups`).snapshotChanges();
	}

	organizationGroup$<T>(organizationCode: string, groupCode: string): Observable<T> {
		return this.object<T>(`/organizations/${organizationCode}/groups/${groupCode}`).valueChanges();
	}

	setOrganizationGroup(organizationCode: string, groupCode: string, value: any): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}`).set(value);
	}

	updateOrganizationGroup(organizationCode: string, groupCode: string, patch: any): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}`).update(patch);
	}

	removeOrganizationGroup(organizationCode: string, groupCode: string): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}`).remove();
	}

	organizationGroupCustomField$<T>(organizationCode: string, groupCode: string, name: string): Observable<T> {
		return this.object<T>(`/organizations/${organizationCode}/groups/${groupCode}/meta/customFields/${name}`).valueChanges();
	}

	setOrganizationGroupCustomField(organizationCode: string, groupCode: string, name: string, value: any): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}/meta/customFields/${name}`).set(value);
	}

	removeOrganizationGroupCustomField(organizationCode: string, groupCode: string, name: string): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}/meta/customFields/${name}`).remove();
	}

	setOrganizationGroupCondition(organizationCode: string, groupCode: string, kind: string, conditions: string): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}/meta/conditions/${kind}`).set(conditions);
	}

	organizationGroupService$<T>(organizationCode: string, groupCode: string, serviceId: string | number): Observable<T> {
		return this.object<T>(`/organizations/${organizationCode}/groups/${groupCode}/services/${serviceId}`).valueChanges();
	}

	setOrganizationGroupService(organizationCode: string, groupCode: string, serviceId: string | number, value: any): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}/services/${serviceId}`).set(value);
	}

	updateOrganizationGroupService(organizationCode: string, groupCode: string, serviceId: string | number, patch: any): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}/services/${serviceId}`).update(patch);
	}

	removeOrganizationGroupService(organizationCode: string, groupCode: string, serviceId: string | number): Promise<void> {
		return this.object(`/organizations/${organizationCode}/groups/${groupCode}/services/${serviceId}`).remove();
	}

	services$<T>(): Observable<T[]> {
		return this.list<T>('/services').valueChanges();
	}

	service$<T>(id: string | number): Observable<T> {
		return this.object<T>(`/services/${id}`).valueChanges();
	}

	updateService(id: string | number, patch: any): Promise<void> {
		return this.object(`/services/${id}`).update(patch);
	}

	removeService(id: string | number): Promise<void> {
		return this.object(`/services/${id}`).remove();
	}

	servicesMeta$<T>(): Observable<T> {
		return this.object<T>('/services-meta').valueChanges();
	}

	setServiceMeta(id: string | number, value: any): Promise<void> {
		return this.object(`/services-meta/${id}`).set(value);
	}

	vehicleBrands$(year: string): Observable<any[]> {
		return this.list(`/vehicles/brand/${year}`).valueChanges();
	}

	vehicleModels$(brand: string, year: string): Observable<any[]> {
		return this.list(`/vehicles/model/${brand}/${year}`).valueChanges();
	}

	vehicleModelsSnapshot$(brand: string, year: string) {
		return this.list(`/vehicles/model/${brand}/${year}`).snapshotChanges();
	}

	vehicleYears$(): Observable<any[]> {
		return this.list('/vehicles/year').valueChanges();
	}

	parks$<T>(): Observable<T[]> {
		return this.list<T>('/park').valueChanges();
	}

	parkMeta$(): Observable<any> {
		return this.object(`/park-meta`).valueChanges();
	}

	sectorsSnapshot$() {
		return this.list('/sectors').snapshotChanges();
	}

	pushSector(data: any) {
		return this.list('/sectors').push(data);
	}

	updateSector(key: string, sector: any): Promise<void> {
		return this.list('/sectors').update(key, sector);
	}

	removeSector(key?: string): Promise<void> {
		return this.list('/sectors').remove(key);
	}

	faq$<T>(): Observable<T> {
		return this.object<T>('/faq').valueChanges();
	}

	pushFaqItem(organization: string, data: any) {
		return this.list(`/faq/${organization}/list`).push(data);
	}

	updateFaqItem(organization: string, id: string, data: any): Promise<void> {
		return this.object(`/faq/${organization}/list/${id}`).update(data);
	}

	removeFaqItem(organization: string, id: string): Promise<void> {
		return this.object(`/faq/${organization}/list/${id}`).remove();
	}

	pushFaqGroup(organization: string, data: any) {
		return this.list(`/faq/${organization}/groups`).push(data);
	}

	updateFaqGroup(organization: string, id: string, data: any): Promise<void> {
		return this.object(`/faq/${organization}/groups/${id}`).update(data);
	}

	removeFaqGroup(organization: string, id: string): Promise<void> {
		return this.object(`/faq/${organization}/groups/${id}`).remove();
	}

	config$(): Observable<any> {
		return this.object('/config').valueChanges();
	}

	/** Org scopes currently redirected to the maintenance site — see MaintenanceScopeRedirectService. */
	maintenanceScopes$(): Observable<string[]> {
		return this.object<string[]>('/config/maintenanceScopes').valueChanges();
	}

	/** The single org scope currently force-logged-out and sent home (null when none is) — see
	 *  KillSwitchScopeRedirectService. */
	killSwitchScope$(): Observable<string | null> {
		return this.object<string | null>('/config/killSwitchScope').valueChanges();
	}

	setMonerisConfig(data: any): Promise<void> {
		return this.object(`/config/moneris`).set(data);
	}

	cardholderReport$(): Observable<any> {
		return this.object('/upload/CardholderReport').valueChanges();
	}

	setCardholderReport(date: any): Promise<void> {
		return this.object('/upload/CardholderReport').set(date);
	}

	translations$(lang: string, prefix = 'language/'): Observable<any> {
		return this.object(`${prefix}${lang}`).valueChanges();
	}

	language$(lang: string): Observable<any> {
		return this.object(`/language/${lang}`).valueChanges();
	}

	setLanguageString(id: string, value: string, lang = 'fr'): Promise<void> {
		return this.object(`/language/${lang}/${id}`).set(value);
	}

	logStories$<T>(): Observable<T> {
		return this.object<T>('/logStories').valueChanges();
	}

	setCemRoles(uid: string, value: string): Promise<void> {
		return this.object(`/cemRoles/${uid}/roles`).set(value);
	}

	capacitySystem$<T>(): Observable<T> {
		return this.object<T>('/capacitySystem').valueChanges();
	}

	decrementCapacity(serviceId: number) {
		const ref = this.db.database.ref(`/capacity/${serviceId}`);
		return ref.transaction(currentValue => {
			if (currentValue === null || currentValue === undefined) {
				return 0;
			}
			if (typeof currentValue !== 'number') {
				console.error('Valeur de capacity invalide:', currentValue);
				return currentValue;
			}
			if (currentValue > 0) {
				return currentValue - 1;
			}
			return currentValue;
		});
	}

	reserveCapacity(newServiceId: number, uid: string) {
		const ref = this.db.database.ref('/capacitySystem');
		return ref.transaction((root: any) => {
			if (root == null) {
				root = { counters: {}, bookings: {} };
			}

			const counters = root.counters || {};
			const bookings = root.bookings || {};
			const prevServiceId = bookings[uid]?.availableServiceId || null;

			if (prevServiceId === newServiceId) {
				return root;
			}

			const newCap = Number(counters[newServiceId] ?? 0);
			if (newCap <= 0) {
				return;
			}

			counters[newServiceId] = newCap - 1;
			if (prevServiceId) {
				counters[prevServiceId] = Number(counters[prevServiceId] ?? 0) + 1;
			}
			bookings[uid] = { availableServiceId: newServiceId };
			root.counters = counters;
			root.bookings = bookings;
			return root;
		});
	}

	statusPlates$(): Observable<any[]> {
		return this.list('/status/plates', ref => ref.orderByChild('found')).valueChanges();
	}

	derogationsSpaq$(): Observable<any[]> {
		return this.list('/derogations-spaq', ref => ref.orderByChild('created')).valueChanges();
	}

	derogations$(): Observable<any[]> {
		return this.list('/derogations', ref => ref.orderByChild('created')).valueChanges();
	}

	derogationsObject$(): Observable<any> {
		return this.object('/derogations').valueChanges();
	}

	updateDerogations(value: any): Promise<void> {
		return this.object('/derogations').update(value);
	}

	setDerogationSpaq(id: string, value: any): Promise<void> {
		return this.object(`/derogations-spaq/${id}`).set(value);
	}

	removeDerogationSpaq(id: string): Promise<void> {
		return this.object(`/derogations-spaq/${id}`).remove();
	}
}
